import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import NextComparisonLink from 'components/NextComparisonLink'
import ComparisonEditor from 'components/ComparisonEditor';
import { ArrowLeft } from 'lucide-react'

const Comparison = () => {
  const { list_id: listId, comparison_id: comparisonId } = useParams();
  const [comparison, setComparison] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false)
  const [justSaved, setJustSaved] = useState(false)
  const [editorLocked, setEditorLocked] = useState(false)
  const [prompting, setPrompting] = useState(false)
  const [promptJustCompleted, setPromptJustCompleted] = useState(false)

  const loadComparison = () => {
    setLoading(true)
    axios.get(`/api/lists/${listId}/comparisons/${comparisonId}`)
      .then(res => {
        setComparison(res.data)
        setLoading(false)
      })
      .catch( err => {
        setError(JSON.stringify(err))
        setLoading(false)
      })
  }

  useEffect(loadComparison, [listId, comparisonId]);

  const updateComparison = (newValues) => {
    setSaving(true)
    axios.put(`/api/lists/${listId}/comparisons/${comparisonId}`, {
      comparison: newValues
    })
      .then(() => {
        setSaving(false)
        setComparison(newValues)
        setJustSaved(true)
        setTimeout( () => setJustSaved(false), 600)
      })
      .catch(() => setError('Failed to save comparison.'));
  };

  const promptMe = (currentReasoning) => {
    // Lock the editor
    setEditorLocked(true);
    setPrompting(true);
    setError(null);

    // First save the comparison to ensure latest changes are captured
    // Use the current reasoning from the editor if provided, otherwise use the comparison state
    const comparisonToSave = {
      ...comparison,
      reasoning: currentReasoning !== undefined ? currentReasoning : comparison.reasoning
    };

    axios.put(`/api/lists/${listId}/comparisons/${comparisonId}`, {
      comparison: comparisonToSave
    })
      .then(() => {
        // After successful save, post to create a prompt
        return axios.post(`/api/comparisons/${comparisonId}/prompts`);
      })
      .then(response => {
        const promptId = response.data.id;

        // Poll for completion
        const pollPrompt = () => {
          axios.get(`/api/comparison_prompts/${promptId}`)
            .then(pollResponse => {
              const status = pollResponse.data.status;

              if (status === 'completed') {
                // Reload the comparison
                loadComparison();
                setEditorLocked(false);
                setPrompting(false);
                setPromptJustCompleted(true);
                // Reset the flag after a short delay to allow the scroll to happen
                setTimeout(() => setPromptJustCompleted(false), 200);
              } else if (status === 'failed') {
                setError('Prompt generation failed. Please try again.');
                setEditorLocked(false);
                setPrompting(false);
              } else {
                // Still processing, poll again in 2 seconds
                setTimeout(pollPrompt, 2000);
              }
            })
            .catch(pollError => {
              setError('Failed to check prompt status.');
              setEditorLocked(false);
              setPrompting(false);
            });
        };

        // Start polling
        pollPrompt();
      })
      .catch(error => {
        setError('Failed to save comparison or create prompt. Please try again.');
        setEditorLocked(false);
        setPrompting(false);
      });
  }

  if (loading) return <p>Loading next comparison...</p>;
  if (error) return <div className="alert alert-warning">{error}</div>;

  return (
    <>
      <div className='row align-items-start'>
        <div className='col md-6'>
          <Link to={`/lists/${listId}`}>
            <ArrowLeft />
            List: {comparison.list.name}
          </Link>
        </div>
        <div className='col md-6 d-flex justify-content-end'>
          <NextComparisonLink listId={listId} />
        </div>
      </div>
      <div className='mt-4'>
        <ComparisonEditor
          comparison={comparison}
          onSave={updateComparison}
          saving={saving}
          justSaved={justSaved}
          promptMe={promptMe}
          editorLocked={editorLocked}
          prompting={prompting}
          onPromptCompleted={promptJustCompleted}
          listId={listId}
        />
      </div>
    </>
  );
};

export default Comparison;
